import React, { useState, useEffect } from 'react';
import { getCached, setCached, TTL } from '../utils/apiCache';

function APIS({ setGlobalData, refreshPvtData }) {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
    const [isLoadingPublic, setIsLoadingPublic] = useState(true);
    const [isLoadingPrivate, setIsLoadingPrivate] = useState(isLoggedIn);

    const [error, setError] = useState(null);

    // Define API data states
    const [apiData, setApiData] = useState({
        sliders: { main: [], favourite: [], feature: [] },
        socialLinks: [],
        general: {},
        paymentMethods: [],
        withdrawMethods: [],
        hasEWallet: false,
        promotions: { activePromotion: [], displayPromotion: [], allPromotions: [] },
        userData: null,
        transactions: [],
        turnovers: [],
        marketBlock: [],
    });

    // Fetch Public APIs (No login required)
    useEffect(() => {
        const fetchPublicData = async () => {
            setIsLoadingPublic(true);

            // ── Serve from sessionStorage cache if still fresh ────────────────────
            const publicCache = getCached('public_apis');
            if (publicCache) {
                setApiData(prevData => ({ ...prevData, ...publicCache }));
                setIsLoadingPublic(false);
                return;
            }

            try {
                let headers = {};
                const token = localStorage.getItem('token');
                let hasEWallet = false;
                if (token) {
                    headers = { Authorization: `Bearer ${token}` };
                }

                const apiResponses = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/sliders`, { headers: headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/sociallink`, { headers: headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/general`, { headers: headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/payment-methods`, { headers: headers }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/promotion`, { headers: headers }).then(res => res.json()),
                ]);

                const [slidersRaw, socialData, generalData, paymentData, promotionsData] = apiResponses;

                // ── Normalise sliders response: handle both array and { data: [] } formats
                const slidersData = Array.isArray(slidersRaw)
                    ? slidersRaw
                    : Array.isArray(slidersRaw?.data)
                        ? slidersRaw.data
                        : [];

                // ── Normalise payment-methods response ──────────────────────────
                // API may return: plain array, { data: [...] }, { type:'ewallet', pgs:[...] },
                // or { success:true, data:[...] }
                let pgs = [];
                if (paymentData?.type === 'ewallet') {
                    // eWallet mode: payment methods are inside .pgs
                    pgs = Array.isArray(paymentData?.pgs) ? paymentData.pgs : [];
                    hasEWallet = true;
                } else if (Array.isArray(paymentData)) {
                    // Plain array response
                    pgs = paymentData;
                    hasEWallet = false;
                } else if (Array.isArray(paymentData?.data)) {
                    // Wrapped { data: [...] } response
                    pgs = paymentData.data;
                    hasEWallet = false;
                } else {
                    // Unknown format — fallback to empty array
                    console.warn('Unexpected payment-methods API format:', paymentData);
                    pgs = [];
                    hasEWallet = false;
                }

                // ── Case-insensitive filter so 'main', 'Main', 'MAIN' all match
                const filterSliders = (type) => slidersData.filter(
                    s => s.status?.toLowerCase() === 'active' && s.slider_type?.toLowerCase() === type.toLowerCase()
                );

                const freshData = {
                    hasEWallet: hasEWallet,
                    sliders: {
                        main: filterSliders('Main'),
                        favourite: filterSliders('Favourite'),
                        feature: filterSliders('Features'),
                    },
                    socialLinks: socialData[0] || {},
                    general: generalData[0] || {},
                    paymentMethods: pgs,
                    promotions: {
                        activePromotion: Array.isArray(promotionsData) ? promotionsData.filter(promo => promo.status === 'active') : [],
                        displayPromotion: Array.isArray(promotionsData) ? promotionsData.filter(promo => promo.status === 'display') : [],
                        allPromotions: Array.isArray(promotionsData) ? promotionsData : [],
                    },
                };

                // Cache general separately so App.jsx can read it without a second fetch
                setCached('general', generalData, TTL.public);
                // Only cache when sliders loaded successfully — avoids caching empty data
                // and forcing stale results for 5 min when the API was slow/errored.
                if (freshData.sliders.main.length > 0 || freshData.sliders.feature.length > 0) {
                    setCached('public_apis', freshData, TTL.public);
                }

                setApiData(prevData => ({
                    ...prevData,
                    ...freshData,
                }));

            } catch (error) {
                console.error('Error fetching public data:', error);
                setError('Error fetching public API data');
            } finally {
                setIsLoadingPublic(false);
            }
        };

        fetchPublicData();
    }, []);

    // Track whether this is the initial fetch
    const [isInitialFetch, setIsInitialFetch] = useState(true);

    // Fetch Private APIs (Login required)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const remoteId = localStorage.getItem('remote_id');

        if (isLoggedIn && token && remoteId) {
            setIsLoadingPrivate(true);

            const fetchPrivateData = async () => {
                try {
                    // On initial load, fetch everything. On refreshPvtData toggle, fetch only user+transactions.
                    const fetches = [
                        fetch(`${process.env.REACT_APP_API_URL}/api/user/show?remote_id=${remoteId}`, {
                            method: 'GET',
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/transaction/show?remote_id=${remoteId}`, {
                            method: 'GET',
                            headers: { Authorization: `Bearer ${token}` },
                        }).then(res => res.json()),
                    ];

                    // Only fetch turnovers & marketBlock on initial load
                    if (isInitialFetch) {
                        fetches.push(
                            fetch(`${process.env.REACT_APP_API_URL}/api/turnovers?remote_id=${remoteId}`, {
                                method: 'GET',
                                headers: { Authorization: `Bearer ${token}` },
                            }).then(res => res.json()),
                            fetch(`${process.env.REACT_APP_API_URL}/api/market/block`, {
                                method: 'GET',
                                headers: { Authorization: `Bearer ${token}` },
                            }).then(res => res.json())
                        );
                    }

                    const results = await Promise.all(fetches);
                    const userData = results[0];
                    const transactions = results[1];

                    if (isInitialFetch) {
                        const turnovers = results[2];
                        const marketBlock = results[3];
                        setApiData(prevData => ({
                            ...prevData,
                            userData,
                            transactions,
                            turnovers: turnovers?.turnover || [],
                            marketBlock: marketBlock || [],
                        }));
                        setIsInitialFetch(false);
                    } else {
                        setApiData(prevData => ({
                            ...prevData,
                            userData,
                            transactions,
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching private data:', error);
                } finally {
                    setIsLoadingPrivate(false);
                }
            };

            fetchPrivateData();
        }
    }, [isLoggedIn, refreshPvtData]);

    // Pass API data to global state in App.js
    useEffect(() => {
        setGlobalData(apiData);
    }, [apiData, setGlobalData]);

    // Render nothing — let the UI show immediately with available data.
    // Individual components handle their own loading/empty states.
    return null;
}

export default APIS;
